import { useState, useCallback, useEffect, useRef } from 'react';
import { gbtaServiceInstance, GBTAUploadData } from '../services/gbtaService';
import { useToast } from './useToast';

interface UseGBTAOptions {
  hotelId: string;
  skipInitialFetch?: boolean;
}

interface UseGBTAReturn {
  gbtaData: GBTAUploadData | null;
  gbtaLoading: boolean;
  gbtaError: string | null;
  isUploading: boolean;
  isDeleting: boolean;
  fetchGbta: () => Promise<void>;
  uploadGbta: (file: File, onProgress?: (progress: number) => void) => Promise<void>;
  deleteGbta: (attachmentId: string) => Promise<void>;
  clearError: () => void;
}

export const useGBTA = ({
  hotelId,
  skipInitialFetch = false,
}: UseGBTAOptions): UseGBTAReturn => {
  const [gbtaData, setGbtaData] = useState<GBTAUploadData | null>(null);
  const [gbtaLoading, setGbtaLoading] = useState(!skipInitialFetch);
  const [gbtaError, setGbtaError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  /**
   * Fetch GBTA data for the hotel
   */
  const fetchGbta = useCallback(async () => {
    if (!hotelId) return;

    setGbtaLoading(true);
    setGbtaError(null);

    try {
      const data = await gbtaServiceInstance.fetchGbtaData(hotelId);
      setGbtaData(data);
    } catch (err: any) {
      // Don't show error toast for 404 - it just means no GBTA data exists yet
      const is404 = err.response?.status === 404;
      const errorMessage =
        err.response?.data?.message || 
        err.message || 
        'Failed to fetch GBTA data';
      setGbtaError(errorMessage);
      setGbtaData(null);
      if (!is404) {
        toastRef.current.error('Failed to load GBTA data');
      }
    } finally {
      setGbtaLoading(false);
    }
  }, [hotelId]);

  /**
   * Upload a GBTA file
   */
  const uploadGbta = useCallback(
    async (file: File, onProgress?: (progress: number) => void) => {
      if (!hotelId) {
        throw new Error('Hotel ID is required');
      }

      setIsUploading(true);
      setGbtaError(null);

      try {
        const uploadedData = await gbtaServiceInstance.uploadGbtaFile(
          hotelId,
          file,
          (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              onProgress(progress);
            }
          }
        );

        setGbtaData(uploadedData);
        toastRef.current.success(`File ${file.name} uploaded successfully!`);
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 
          err.message || 
          'Upload failed';
        setGbtaError(errorMessage);
        toastRef.current.error(`Failed to upload ${file.name}: ${errorMessage}`);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [hotelId]
  );

  /**
   * Delete a GBTA attachment
   */
  const deleteGbta = useCallback(
    async (attachmentId: string) => {
      if (!hotelId) {
        throw new Error('Hotel ID is required');
      }

      setIsDeleting(true);
      setGbtaError(null);

      try {
        await gbtaServiceInstance.deleteGbtaFile(hotelId, attachmentId);
        setGbtaData(null);
        toastRef.current.success('GBTA attachment deleted successfully');
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 
          err.message || 
          'Failed to delete GBTA attachment';
        setGbtaError(errorMessage);
        toastRef.current.error(errorMessage);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [hotelId]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setGbtaError(null);
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    if (!skipInitialFetch && hotelId) {
      fetchGbta();
    }
  }, [hotelId, skipInitialFetch, fetchGbta]);

  return {
    gbtaData,
    gbtaLoading,
    gbtaError,
    isUploading,
    isDeleting,
    fetchGbta,
    uploadGbta,
    deleteGbta,
    clearError,
  };
};